import { DateTime } from 'luxon';

import { useMemo, useState } from 'react';

import {
  IconBuilding,
  IconCheck,
  IconGridDots,
  IconSearch,
  IconTrendingUp,
} from '@tabler/icons-react';

import { createFileRoute } from '@tanstack/react-router';

import {
  EmptyStateView,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  StandardLayoutView,
  StatTileView,
  Text,
  TextInput,
  UnstyledButton,
} from '@optiro/ui';

import { useCreatePartnerModal } from '@/components/CreatePartnerForm/useCreatePartnerModal';
import { PartnerCard } from '@/components/PartnerCard';
import { usePartners } from '@/hooks/usePartners/usePartners';

export const Route = createFileRoute('/app/partners/')({
  component: RouteComponent,
});

type CategoryFilter = { id: string; name: string; color: string };

function CategoryChip({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color?: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: 999,
        border: `1.5px solid ${active ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-3)'}`,
        backgroundColor: active ? 'var(--mantine-color-dark-6)' : 'transparent',
        color: active ? '#fff' : 'var(--mantine-color-dark-6)',
        fontSize: 'var(--mantine-font-size-sm)',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'border-color 120ms, background-color 120ms',
      }}
    >
      {color && (
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: active ? '#fff' : color,
            flexShrink: 0,
          }}
        />
      )}
      {label}{' '}
      <Text component="span" size="sm" style={{ opacity: 0.7, fontWeight: 400 }}>
        {count}
      </Text>
    </UnstyledButton>
  );
}

function RouteComponent() {
  const { data, isLoading } = usePartners();
  const { openCreatePartnerModal, drawer } = useCreatePartnerModal();
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const partners = useMemo(() => data ?? [], [data]);

  const stats = useMemo(() => {
    const total = partners.length;
    const last30Days = DateTime.now().minus({ days: 30 });
    const newLast30 = partners.filter((p) => DateTime.fromISO(p.created_at) >= last30Days).length;

    const categoryIds = new Set(partners.map((p) => p.company_account_category_id).filter(Boolean));

    const withRate = partners.filter((p) => p.hourly_rate != null);
    const avgRate =
      withRate.length > 0
        ? Math.round(
            withRate.reduce((sum, p) => sum + parseFloat(String(p.hourly_rate ?? 0)), 0) /
              withRate.length
          )
        : 0;

    const thisMonthStart = DateTime.now().startOf('month');
    const activeThisMonth = partners.filter(
      (p) => p.last_used_at && DateTime.fromISO(p.last_used_at) >= thisMonthStart
    ).length;

    return {
      total,
      newLast30,
      categoryCount: categoryIds.size,
      avgRate,
      activeThisMonth,
    };
  }, [partners]);

  const categories = useMemo<CategoryFilter[]>(() => {
    const seen = new Map<string, CategoryFilter>();
    for (const p of partners) {
      if (p.company_account_category_id && p.company_account_category) {
        seen.set(p.company_account_category_id, {
          id: p.company_account_category_id,
          name: p.company_account_category.name,
          color: p.company_account_category.color,
        });
      }
    }
    return Array.from(seen.values());
  }, [partners]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return partners.filter((p) => {
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.contact_name?.toLowerCase().includes(q) ||
        p.contact_email?.toLowerCase().includes(q);
      const matchesCategory =
        !selectedCategoryId || p.company_account_category_id === selectedCategoryId;
      return matchesSearch && matchesCategory;
    });
  }, [partners, search, selectedCategoryId]);

  const statsRow = (
    <SimpleGrid cols={{ base: 2, md: 4 }}>
      <StatTileView
        label="Partnere i alt"
        value={stats.total}
        icon={IconBuilding}
        tone={stats.newLast30 > 0 ? 'positive' : 'neutral'}
        delta={
          stats.newLast30 > 0
            ? { value: `+${stats.newLast30} sidste 30 dage`, direction: 'up' }
            : undefined
        }
      />
      <StatTileView label="Kategorier" value={stats.categoryCount} icon={IconGridDots} />
      <StatTileView
        label="Gns. timepris"
        value={stats.avgRate > 0 ? `${stats.avgRate} kr` : '—'}
        icon={IconTrendingUp}
      />
      <StatTileView
        label="Aktive samarbejder"
        value={stats.activeThisMonth}
        icon={IconCheck}
        tone="positive"
        delta={{ value: 'denne måned', direction: 'flat' }}
      />
    </SimpleGrid>
  );

  const filtersRow = (
    <Stack gap="sm">
      <Group gap="md" wrap="nowrap">
        <TextInput
          placeholder="Søg på partner, kontaktperson eller e-mail..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
      </Group>

      <Group gap="xs">
        <CategoryChip
          label="Alle"
          count={partners.length}
          active={selectedCategoryId === null}
          onClick={() => setSelectedCategoryId(null)}
        />
        {categories.map((cat) => {
          const count = partners.filter((p) => p.company_account_category_id === cat.id).length;
          return (
            <CategoryChip
              key={cat.id}
              label={cat.name}
              count={count}
              color={cat.color}
              active={selectedCategoryId === cat.id}
              onClick={() => setSelectedCategoryId(selectedCategoryId === cat.id ? null : cat.id)}
            />
          );
        })}
      </Group>
    </Stack>
  );

  const isError = !data && !isLoading;

  const content = (() => {
    if (isLoading) {
      return (
        <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} height={60} radius="md" />
          ))}
        </SimpleGrid>
      );
    }

    if (isError) {
      return (
        <EmptyStateView
          variant="feature"
          title="An error occurred while loading data"
          description="Please try refreshing the page or contact support if the problem persists."
        />
      );
    }

    if (filtered.length === 0) {
      return (
        <EmptyStateView
          variant="feature"
          title="No data found"
          description="There are no items to display at the moment."
        />
      );
    }

    return (
      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }}>
        {filtered.map((partner) => (
          <PartnerCard key={partner.id} partner={partner} />
        ))}
      </SimpleGrid>
    );
  })();

  return (
    <>
      {drawer}
      <StandardLayoutView
        title="Partnere"
        primaryAction={{ label: 'Opret partner', onClick: openCreatePartnerModal }}
        toolbar={filtersRow}
      >
        <Stack gap="md">
          {statsRow}
          {content}
        </Stack>
      </StandardLayoutView>
    </>
  );
}
